/**
 * BattleController Component
 *
 * Replays server-simulated battles tick-by-tick
 * Reads battleLog from server and updates battleState for display
 *
 * Uses logIndex to track position in the log array (separate from tick numbers)
 * to handle ties where the log has extra entries for the same tick.
 *
 * When a tie is detected (isTie entry), playback pauses to allow the UI
 * to display "TIE!" and "RUN IT BACK!" before continuing.
 */

import { useEffect, useRef } from 'react';
import { useGame } from '../contexts/GameContext';
import { GAME_CONFIG } from '../shared/gameData';

const BattleController = () => {
  const { battleState, setBattleState, battleSpeed } = useGame();
  const initialTickProcessed = useRef(false);
  const tiePauseTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tiePauseTimeoutRef.current) {
        clearTimeout(tiePauseTimeoutRef.current);
      }
    };
  }, []);

  // Process first log entry (index 0) immediately when battle starts
  useEffect(() => {
    if (!battleState || !battleState.serverResult || !battleState.log || battleState.log.length === 0) {
      initialTickProcessed.current = false;
      return;
    }

    // Only process first entry once, and only if displayLog is empty
    // Use logIndex to track array position (default to 0 if not set)
    const currentLogIndex = battleState.logIndex ?? 0;
    if (currentLogIndex === 0 && !initialTickProcessed.current && battleState.displayLog?.length === 0) {
      initialTickProcessed.current = true;

      const logEntry = battleState.log[0];
      if (logEntry) {
        let message = logEntry.message || '';
        const playerName = battleState.player?.name || 'Player 1';
        const opponentName = battleState.opponent?.name || 'Player 2';
        message = message
          .replace(/Player 1/g, playerName)
          .replace(/Player 2/g, opponentName);

        let type = 'normal';
        if (logEntry.isTie) type = 'tie';
        else if (message.includes('CRITICAL HIT')) type = 'crit';
        else if (message.includes('Victory!')) type = 'victory';
        else if (message.includes('defeated')) type = message.includes(battleState.player.name) ? 'defeat' : 'victory';
        else if (message.includes('damage')) type = 'hit';
        else if (message.includes('missed')) type = 'miss';

        if (message) {
          setBattleState(prev => ({
            ...prev,
            logIndex: 0,
            tick: logEntry.tick || 0,
            player: {
              ...prev.player,
              currentHP: logEntry.player1.currentHp,
              currentStamina: logEntry.player1.energy,
              statusEffects: logEntry.player1.statusEffects || []
            },
            opponent: {
              ...prev.opponent,
              currentHP: logEntry.player2.currentHp,
              currentStamina: logEntry.player2.energy,
              statusEffects: logEntry.player2.statusEffects || []
            },
            displayLog: [{ text: message, type: type, tick: logEntry.tick || 0, isTie: logEntry.isTie, weather: logEntry.weather }]
          }));
        }
      }
    }
  }, [battleState?.serverResult, battleState?.log, battleState?.logIndex, battleState?.displayLog?.length, setBattleState]);

  useEffect(() => {
    if (!battleState || !battleState.serverResult || !battleState.log || battleState.log.length === 0) {
      return;
    }

    const currentLogIndex = battleState.logIndex ?? 0;

    // Don't replay if battle log is fully processed
    if (currentLogIndex >= battleState.log.length - 1) {
      return;
    }

    // If paused for tie or KO animation, don't start new interval
    if (battleState.tiePaused || battleState.koPaused) {
      return;
    }

    // Calculate tick duration based on speed and config
    // Use TICK_DURATION_MS from config as base (default 300ms)
    // Then apply speed multiplier (1x = 300ms, 2x = 150ms, 4x = 75ms)
    const baseTick = GAME_CONFIG.BATTLE?.TICK_DURATION_MS || 300;
    const tickDuration = baseTick / battleSpeed;

    const interval = setInterval(() => {
      setBattleState(prev => {
        if (!prev) {
          clearInterval(interval);
          return prev;
        }

        const prevLogIndex = prev.logIndex ?? 0;

        // Stop if we've reached the end of the log
        if (prevLogIndex >= prev.log.length - 1) {
          clearInterval(interval);
          return prev;
        }

        const nextLogIndex = prevLogIndex + 1;
        const logEntry = prev.log[nextLogIndex];

        if (!logEntry) {
          clearInterval(interval);
          return prev;
        }

        // Convert current tick's message to display format
        let message = logEntry.message || '';

        // Replace "Player 1" and "Player 2" with actual Pokemon names
        const playerName = prev.player?.name || 'Player 1';
        const opponentName = prev.opponent?.name || 'Player 2';
        message = message
          .replace(/Player 1/g, playerName)
          .replace(/Player 2/g, opponentName);

        let type = 'normal';

        if (logEntry.isTie) type = 'tie';
        else if (message.includes('CRITICAL HIT')) type = 'crit';
        else if (message.includes('Victory!')) type = 'victory';
        else if (message.includes('defeated')) type = message.includes(prev.player.name) ? 'defeat' : 'victory';
        else if (message.includes('damage')) type = 'hit';
        else if (message.includes('missed')) type = 'miss';

        // Add current message to display log (only if there's a message)
        const newDisplayLog = message
          ? [...(prev.displayLog || []), { text: message, type: type, tick: logEntry.tick, isTie: logEntry.isTie, weather: logEntry.weather }]
          : (prev.displayLog || []);

        // Check if this is a tie entry - if so, pause playback for KO animation with fakeout
        // The KO animation will show the fakeout sequence, then BattleScreen handles "RUN IT BACK!"
        if (logEntry.isTie) {
          clearInterval(interval);
          return {
            ...prev,
            logIndex: nextLogIndex,
            tick: logEntry.tick,
            // Set HP to 0 to show the KO (tie entry has both at 0)
            player: {
              ...prev.player,
              currentHP: 0,
              currentStamina: logEntry.player1.energy,
              statusEffects: logEntry.player1.statusEffects || []
            },
            opponent: {
              ...prev.opponent,
              currentHP: 0,
              currentStamina: logEntry.player2.energy,
              statusEffects: logEntry.player2.statusEffects || []
            },
            displayLog: newDisplayLog,
            koPaused: true, // Pause for KO animation (changed from tiePaused)
            koData: {
              target: logEntry.koTarget || 'both',
              attacker: logEntry.koAttacker || 'both',
              isTie: true,
              koMove: logEntry.koMove
            }
          };
        }

        // Check if this is a KO entry - if so, pause for KO animation
        if (logEntry.koOccurred && !logEntry.isTie) {
          clearInterval(interval);
          return {
            ...prev,
            logIndex: nextLogIndex,
            tick: logEntry.tick,
            player: {
              ...prev.player,
              currentHP: logEntry.player1.currentHp,
              currentStamina: logEntry.player1.energy,
              statusEffects: logEntry.player1.statusEffects || []
            },
            opponent: {
              ...prev.opponent,
              currentHP: logEntry.player2.currentHp,
              currentStamina: logEntry.player2.energy,
              statusEffects: logEntry.player2.statusEffects || []
            },
            displayLog: newDisplayLog,
            koPaused: true, // Flag to pause playback for KO animation
            koData: {
              target: logEntry.koTarget,
              attacker: logEntry.koAttacker,
              isTie: false,
              koMove: logEntry.koMove
            }
          };
        }

        // Update battle state with data from log entry
        return {
          ...prev,
          logIndex: nextLogIndex,
          tick: logEntry.tick,
          player: {
            ...prev.player,
            currentHP: logEntry.player1.currentHp,
            currentStamina: logEntry.player1.energy,
            statusEffects: logEntry.player1.statusEffects || []
          },
          opponent: {
            ...prev.opponent,
            currentHP: logEntry.player2.currentHp,
            currentStamina: logEntry.player2.energy,
            statusEffects: logEntry.player2.statusEffects || []
          },
          displayLog: newDisplayLog
        };
      });
    }, tickDuration);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleState?.logIndex, battleState?.koPaused, battleSpeed]);

  return null; // This component doesn't render anything
};

export default BattleController;
